// src/components/reports/precon-report-builder/derive-precon-sections.ts
import { planAssetKey } from "@/offline/asset-keys";
import type { TileStatus } from "@/types/api";
import type {
  PreconSelectedPhoto,
  PreconSelectedRecord,
} from "./precon-types";
import { subLetter } from "./sub-letter";

/**
 * One rendered photo entry — the report's unit. A multi-photo record produces
 * several of these (one per photo), all sharing the record's single pin and
 * number; they differ only by `image_id` / sources / `subLetter`. The flat
 * `located` / `unpinned` / `unlocated` lists below are arrays of these so the
 * section card, plan-with-pins, lightbox and preview all read one shape.
 */
export interface DerivedPhotoEntry {
  /** Owning record id — the pin identity (one marker per record). */
  record_id: string;
  /** ProjectImage id — the caption key, the React key, the lightbox identity. */
  image_id: string;
  thumbnail: string | null;
  image: string | null;
  taken_at: string | null;
  // Inherited from the owning record, shared across its photos:
  pin_x: number | null;
  pin_y: number | null;
  pin_bearing: number | null;
  remark: string;
  /**
   * The record's 1-based number among located records of its section (the plan
   * pin's number). `0` for unpinned/no-plan entries — their visible number is
   * the document-wide `U#` assigned by {@link deriveUnlocatedNumbers}.
   */
  recordNumber: number;
  /**
   * The photo's sub-letter within its record (`a, b, …`), or `null` when the
   * record has a single photo (then the bare number reads, no letter).
   */
  subLetter: string | null;
}

export interface DerivedSection {
  plan_id: string;
  planName: string;
  /** Located photo entries (plan + pin), record-capture order, photos sub-lettered. */
  located: DerivedPhotoEntry[];
  /** Plan-but-unpinned photo entries — capture order (the document's U# walk). */
  unpinned: DerivedPhotoEntry[];
}

export interface DerivedSelection {
  sections: DerivedSection[];
  /** No-plan photo entries — capture order (continues the U# walk). */
  unlocated: DerivedPhotoEntry[];
}

const hasPin = (r: PreconSelectedRecord): boolean =>
  r.pin_x !== null && r.pin_y !== null;

/** A record's capture time for ordering = its first photo's `taken_at`. */
function recordTakenAt(r: PreconSelectedRecord): string | null {
  return r.photos.find((p) => p.taken_at !== null)?.taken_at ?? null;
}

// Ascending by taken_at; nulls sort last (stable for equal/!both keys).
function compareTakenAt(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a < b ? -1 : 1;
}

function byRecordTakenAt(
  a: PreconSelectedRecord,
  b: PreconSelectedRecord,
): number {
  return compareTakenAt(recordTakenAt(a), recordTakenAt(b));
}

/** Photos of a record, capture-time ascending — the sub-letter order. */
function orderedPhotos(r: PreconSelectedRecord): PreconSelectedPhoto[] {
  return [...r.photos].sort((p, q) => compareTakenAt(p.taken_at, q.taken_at));
}

/**
 * Flatten one record into its photo entries. Every photo becomes an entry
 * carrying the record's shared pin/remark; a multi-photo record sub-letters
 * its photos (`a, b, …` in capture order), a single-photo record gets `null`.
 * `recordNumber` is the located pin number (0 for unpinned/no-plan).
 */
function entriesForRecord(
  r: PreconSelectedRecord,
  recordNumber: number,
): DerivedPhotoEntry[] {
  const photos = orderedPhotos(r);
  const multi = photos.length > 1;
  return photos.map((photo, i) => ({
    record_id: r.id,
    image_id: photo.image_id,
    thumbnail: photo.thumbnail,
    image: photo.image,
    taken_at: photo.taken_at,
    pin_x: r.pin_x,
    pin_y: r.pin_y,
    pin_bearing: r.pin_bearing,
    remark: r.remark,
    recordNumber,
    subLetter: multi ? subLetter(i) : null,
  }));
}

export function derivePreconSections(
  records: PreconSelectedRecord[],
  plans: ReadonlyArray<{ id: string; name: string }>,
): DerivedSelection {
  const nameById = new Map(plans.map((p) => [p.id, p.name]));
  const noPlan: PreconSelectedRecord[] = [];
  const byPlan = new Map<string, PreconSelectedRecord[]>();

  for (const r of records) {
    if (r.plan === null) {
      noPlan.push(r);
      continue;
    }
    const bucket = byPlan.get(r.plan);
    if (bucket) bucket.push(r);
    else byPlan.set(r.plan, [r]);
  }

  const sections: DerivedSection[] = [...byPlan.entries()]
    .map(([plan_id, recs]) => {
      // Located records numbered 1..N by capture time — the number is the plan
      // pin's, shared by that record's photos. Unpinned records carry no pin
      // number (their visible number is the document-wide U#).
      const locatedRecords = recs.filter(hasPin).sort(byRecordTakenAt);
      const unpinnedRecords = recs.filter((r) => !hasPin(r)).sort(byRecordTakenAt);
      return {
        plan_id,
        planName: nameById.get(plan_id) ?? plan_id,
        located: locatedRecords.flatMap((r, i) => entriesForRecord(r, i + 1)),
        unpinned: unpinnedRecords.flatMap((r) => entriesForRecord(r, 0)),
      };
    })
    .sort((a, b) => a.planName.localeCompare(b.planName));

  const unlocated = noPlan
    .sort(byRecordTakenAt)
    .flatMap((r) => entriesForRecord(r, 0));

  return { sections, unlocated };
}

/**
 * Mirror of the document's continuous `U1, U2, …` sequence over not-located
 * **records** (see the builder spec, Behavior § "U# numbering"): walk the plan
 * sections in the user's CURRENT order (`order` is the render order), numbering
 * each section's unpinned records in their displayed (capture-time) order, then
 * continue through General Observations. One U# per record — its photos share
 * the number and differ by `subLetter`. Returns **record id** → U-number; the
 * highest value equals the not-located record count.
 *
 * `order` always covers every derived section (initialised from them and only
 * reordered); an id with no matching section is skipped defensively.
 */
export function deriveUnlocatedNumbers(
  derived: DerivedSelection,
  order: ReadonlyArray<string>,
): Map<string, number> {
  const sectionById = new Map(derived.sections.map((s) => [s.plan_id, s]));
  const numbers = new Map<string, number>();
  let next = 1;
  // One number per record: entries of a record are consecutive, so number a
  // record_id the first time it is seen and skip its later photo entries.
  const number = (entries: DerivedPhotoEntry[]) => {
    for (const entry of entries) {
      if (!numbers.has(entry.record_id)) numbers.set(entry.record_id, next++);
    }
  };
  for (const planId of order) {
    const section = sectionById.get(planId);
    if (!section) continue;
    number(section.unpinned);
  }
  number(derived.unlocated);
  return numbers;
}

/**
 * True for the establishing shot of a multi-photo record — the first photo by
 * `taken_at`, identified by its sub-letter being `null` (single-photo record,
 * which is also its own establishing shot) or `"a"` (the first of many).
 *
 * This is the canonical predicate used by both the Sections-step card and the
 * read-only preview to seed the caption from the record remark. `build-precon-
 * payload.ts` uses position (`i === 0`) rather than sub-letter, which is
 * equivalent because `orderedPhotos` sorts by the same `compareTakenAt` that
 * `entriesForRecord` uses to assign sub-letters — the i=0 entry is always the
 * "a" entry. That invariant is documented at the call site.
 */
export function isEstablishingShot(entry: DerivedPhotoEntry): boolean {
  return entry.subLetter === null || entry.subLetter === "a";
}

/** Resolved plan image for a section — null when the plan has no usable image. */
export interface PreconPlanImage {
  assetKey: string;
  src: string;
}

/**
 * The slice of a project plan needed to resolve a section's image —
 * `ProjectPlan` satisfies it; tests can pass minimal literals.
 */
export interface PreconPlanSource {
  id: string;
  latest_revision?: {
    id: string;
    image_url: string | null;
    thumbnail_url: string | null;
    // The tile pipeline replaces a revision's bytes in place when this flips
    // to "ready", so the offline asset key is versioned by it — see
    // `planAssetKey`. Threaded through so the cached plan blob resolves to the
    // same key the image-viewer-modal stores (tiled vs base).
    tile_status?: TileStatus | null;
  } | null;
}

/**
 * Resolve a section's plan image the same way the image-viewer-modal plan
 * thumbnail does: the `plan:{id}:{revisionId}` offline asset key (cached blob
 * preferred by OfflineImage) with the revision's thumbnail-then-full URL as
 * the live source. Null when the plan or its image is unknown — the sheet
 * then degrades exactly like the PDF (metadata + notes, no plan).
 *
 * Pure logic (lives in this module, not the component file, so the
 * plan-with-pins component stays components-only for Fast Refresh). Shared by
 * the Sections-step section card and the read-only preview so both resolve the
 * image identically — no second data path that could drift.
 */
export function resolvePlanImage(
  plan: PreconPlanSource | undefined,
): PreconPlanImage | null {
  if (!plan?.latest_revision) return null;
  const revision = plan.latest_revision;
  const src = revision.thumbnail_url ?? revision.image_url;
  return src
    ? {
        assetKey: planAssetKey(plan.id, revision.id, revision.tile_status),
        src,
      }
    : null;
}
